import React from "react";
import {
  Row,
  Col,
  Upload,
  Collapse,
  Icon,
  Tooltip,
  message,
  Button,
} from "antd";
import XLSIconComponent from "../../../Icons/XLSIconComponent";
import CSVIconComponent from "../../../Icons/CSVIconComponent";
import { useSelector } from "react-redux";
import { userProfiles } from "../../../../redux/slices/UserProfilesSlice";
import { checkSpecialRoleInContractBlock } from "../../../../utils/userRolesUtils";

const { Panel } = Collapse;

export default function MassivePreRegistrationBlock({
  productType,
  serviceType,
  onFileBase64,
  fileUploaded,
  setFileUploaded,
  onSpecialRoleDetected,
  contractFormInfo,
  pkColdchainProduct,
  paqFarmaProduct,
}) {
  const reduxUserRoles = useSelector(userProfiles);
  const hasSpecialRole = checkSpecialRoleInContractBlock(
    contractFormInfo,
    reduxUserRoles?.data,
  );

  const legendConfig = {
    "Frio Activo": {
      default: {
        file: "LeyendaFrioActivo.pdf",
        label: "Leyenda Frio Activo",
      },
    },
    "Frio Pasivo": {
      FRAXQ: {
        file: "LeyendaFrioPasivo_PaqFrio.pdf",
        label: "Leyenda Paq Frio",
      },
      FRAXS: {
        file: "LeyendaFrioPasivo_PaqFarma.pdf",
        label: "Leyenda Paq Farma",
      },
      PADXA: {
        file: "LeyendaFrioPasivo_PaqPremium_Coldchain.pdf",
        label: "Leyenda Paq Premium (Coldchain)",
      },
    },
    "Frio Proximidad": {
      default: {
        file: "LeyendaFrioProximidad.pdf",
        label: "Leyenda Frio Proximidad",
      },
    },
  };

  const templateResolver = {
    "Frio Activo": {
      default: {
        excel: "Plantilla_Masivo_Excel_FrioActivo.xlsx",
        csv: "Plantilla_Masivo_CSV_FrioActivo.csv",
      },
    },
    "Frio Pasivo": {
      FRAXQ: {
        excel: "Plantilla_Masivo_Excel_FrioPasivo_PaqFrio.xlsx",
        csv: "Plantilla_Masivo_CSV_FrioPasivo_PaqFrio.csv",
      },
      FRAXS: {
        excel: "Plantilla_Masivo_Excel_FrioPasivo_PaqFarma.xlsx",
        csv: "Plantilla_Masivo_CSV_FrioPasivo_PaqFarma.csv",
      },
      PADXA: {
        excel: "Plantilla_Masivo_Excel_FrioPasivo_PaqPremium_Coldchain.xlsx",
        csv: "Plantilla_Masivo_CSV_FrioPasivo_PaqPremium_Coldchain.csv",
      },
    },
    "Frio Proximidad": {
      default: {
        excel: "Plantilla_Masivo_Excel_FrioProximidad.xlsx",
        csv: "Plantilla_Masivo_CSV_FrioProximidad.csv",
      },
    },
  };

  const resolveLegendFile = (serviceType, productKey) => {
    if (!serviceType) return null;

    const serviceMap = legendConfig[serviceType];
    if (!serviceMap) return null;

    const productEntry = Object.keys(serviceMap).find(
      (key) => key !== "default" && productKey?.includes(key),
    );

    return serviceMap[productEntry] ?? serviceMap.default;
  };

  const resolveTemplateFiles = (serviceType, productKey) => {
    const serviceConfig = templateResolver[serviceType];
    if (!serviceConfig) return null;

    const productEntry = Object.keys(serviceConfig).find(
      (key) => key !== "excel" && key !== "csv" && productKey?.includes(key),
    );

    return serviceConfig[productEntry] ?? serviceConfig.default;
  };

  const renderLeyenda = () => {
    const legend = resolveLegendFile(serviceType, productType?.key);

    if (!legend) return null;

    return (
      <a href={`/documents/${legend.file}`} download>
        {legend.label}
      </a>
    );
  };

  const renderPlantillas = () => {
    const files = resolveTemplateFiles(serviceType, productType?.key);
    if (!files) return null;

    return (
      <>
        <a href={`/documents/${files.excel}`} download>
          <XLSIconComponent />
        </a>
        <a href={`/documents/${files.csv}`} download>
          <CSVIconComponent />
        </a>
      </>
    );
  };

  const handleFileToBase64 = (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const base64String = e.target.result;

      const cleanBase64 = base64String.split(",")[1];

      message.success("Archivo cargado correctamente.");
      if (typeof onFileBase64 === "function") {
        onFileBase64(cleanBase64);
      }
    };

    reader.onerror = () => {
      message.error("Error al cargar el archivo.");
    };

    reader.readAsDataURL(file);
  };

  const props = {
    name: "file",
    accept:
      ".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel",
    showUploadList: true,
    fileList: fileUploaded?.file ? [fileUploaded.file] : [],
    beforeUpload: (file) => {
      const isValid =
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel" ||
        file.name.endsWith(".csv");

      if (!isValid) {
        message.error("Solo se permiten archivos CSV o Excel.");
        return Upload.LIST_IGNORE;
      }

      if (typeof onSpecialRoleDetected === "function") {
        onSpecialRoleDetected(hasSpecialRole);
      }

      setFileUploaded((prev) => ({
        ...prev,
        file: {
          uid: file.uid,
          name: file.name,
          status: "done",
        },
      }));

      handleFileToBase64(file);
      return false;
    },
    onRemove: () => {
      setFileUploaded((prev) => ({
        ...prev,
        file: null,
      }));
      onFileBase64(null);
    },
    multiple: false,
  };

  return (
    <Row className="form-section">
      <Collapse
        defaultActiveKey={["1"]}
        expandIcon={({ isActive }) => (
          <Icon type="caret-right" rotate={isActive ? 90 : 0} />
        )}
      >
        <Panel
          header="Pre-registro masivo"
          key="1"
          extra={<Icon type="minus-circle" />}
        >
          <Row
            className="pr-massive-box pr-grid-row"
            gutter={[16, 16]}
            type="flex"
          >
            <Col xs={24} md={10} className="pr-col">
              <Upload {...props}>
                <Button icon="upload">Cargar fichero CSV/Excel</Button>
              </Upload>
            </Col>

            <Col xs={24} md={14} className="pr-col">
              <div className="pr-massive-info">
                {renderLeyenda(
                  serviceType,

                  pkColdchainProduct,
                  paqFarmaProduct,
                )}
                {
                  <div className="export-container">
                    <span>Descarga de plantillas de ejemplo:</span>
                    {renderPlantillas(
                      serviceType,
                      pkColdchainProduct,
                      paqFarmaProduct,
                    )}
                  </div>
                }
              </div>
            </Col>
          </Row>
        </Panel>
      </Collapse>
    </Row>
  );
}
